'use client'

import React from 'react'
import { FaApple } from 'react-icons/fa'
import AppStoreLink from '@/app/components/AppStoreLink'

/**
 * Result-aware install ask for the ingredient checker.
 *
 * Why this exists
 * ---------------
 * The ingredient checker is, by a wide margin, the most engaged surface on the
 * site: ~738s average session duration and 64% engagement rate, versus ~150s
 * site-wide. In the 28 days to 2026-07-13, 21 people ran 194 scans — roughly
 * NINE products each, by hand, one paste at a time. It also has the best search
 * CTR on the domain (11.5%, vs ~1.3% site-wide).
 *
 * And it produced 2 app_store_clicks.
 *
 * The old ask was a single generic banner at the very bottom of the results,
 * below every flagged-ingredient card — so on a 7-flag scan it sat several
 * screens down — and it said the same thing whether the reader had just found
 * seven problems or none. It asked nothing of the moment.
 *
 * What changed
 * ------------
 * 1. `placement="inline"` renders directly under the summary card, at the top
 *    of the results, where the eye already is at the instant of peak intent —
 *    the second the reader sees "7 flagged ingredients detected".
 * 2. The copy is built from the reader's OWN result. A 7-flag scan, a 1-flag
 *    scan and a clean scan are three different emotional states and get three
 *    different asks. A clean scan is NOT a failed conversion — it's the moment
 *    to point out that the tool can't see the packaging, which is the bigger
 *    source for many products, and the app can.
 * 3. `scanCount` escalates the ask on repeat use. Someone on their third paste
 *    has *demonstrated the habit the app exists to serve* and is the highest-
 *    intent reader on the site. We name the work they're doing by hand.
 *
 * Each placement/variant reports a distinct `cta_surface` so GA4 can tell us
 * which of these actually converts rather than guessing again.
 */

export interface IngredientCheckerInstallAskProps {
  /** Number of flagged ingredients in the current scan. 0 = clean scan. */
  resultCount: number
  /** High-concern subset of resultCount — drives the strongest copy. */
  highCount: number
  /** How many scans this reader has run this session (1-indexed). */
  scanCount: number
  /** `inline` sits under the summary card; `footer` closes the results list. */
  placement: 'inline' | 'footer'
}

interface AskCopy {
  eyebrow: string
  headline: string
  body: React.ReactNode
  button: string
  /** Stable GA4 label so we can compare variants against each other. */
  surface: string
}

/**
 * Pick the copy from the reader's actual state. Order matters: the repeat-use
 * habit signal outranks the result content, because "you have now done this
 * nine times by hand" is a stronger reason to install than anything we can say
 * about a single scan.
 */
function selectCopy(
  resultCount: number,
  highCount: number,
  scanCount: number,
  placement: 'inline' | 'footer'
): AskCopy {
  // ── Repeat user. The habit is the argument. ──────────────────────────────
  if (scanCount >= 3) {
    return {
      eyebrow: `${scanCount} products checked`,
      headline: `You've hand-checked ${scanCount} products. The app does this from a photo.`,
      body: (
        <>
          Every scan here means finding the ingredient list, copying it, and pasting
          it in. The app skips all of that: <strong>point your camera at the label
          and it reads it for you in about five seconds</strong>, and it also scores
          the <strong>packaging</strong>, which this tool cannot see at all.
        </>
      ),
      button: 'Skip the copy-paste. Get the app',
      surface: `ingredient_checker_repeat_${placement}`,
    }
  }

  // ── Clean scan. Not a dead end — the packaging is the untold half. ───────
  if (resultCount === 0) {
    return {
      eyebrow: 'Clean formula, now check the tube',
      headline: 'The formula is clean. The packaging is the part this tool can’t see.',
      body: (
        <>
          A clean ingredient list is genuinely good news. But for a lot of products the{' '}
          <strong>tube, bottle, or jar is the larger microplastic source</strong>, and no
          ingredient list will ever tell you that. Scan the package with the app and you
          get both halves, formula and container, as one 0–100 score.
        </>
      ),
      button: 'Scan the packaging too',
      surface: `ingredient_checker_clean_${placement}`,
    }
  }

  // ── High-concern hits. Give them the next action, not another warning. ───
  if (highCount > 0) {
    return {
      eyebrow: `${highCount} high-concern ${highCount === 1 ? 'ingredient' : 'ingredients'}`,
      headline: 'Found the problem. Now find what to use instead.',
      body: (
        <>
          Knowing a product is a problem is only half the job, the useful half is knowing
          what to replace it with. Scan the product in the app and you get{' '}
          <strong>a 0–100 risk score, the packaging assessment this tool can&apos;t do,
          and a cleaner same-category swap</strong> you can actually buy.
        </>
      ),
      button: 'Find my cleaner swap',
      surface: `ingredient_checker_high_${placement}`,
    }
  }

  // ── Flagged, but nothing high-concern. Lower the temperature. ────────────
  return {
    eyebrow: `${resultCount} flagged ${resultCount === 1 ? 'ingredient' : 'ingredients'}`,
    headline: 'Nothing high-concern here, but the container still counts.',
    body: (
      <>
        Medium and low-concern flags rarely mean a product is unsafe on their own;
        concentration, rinse-off versus leave-on, and the packaging all matter more.
        The app weighs all three and returns{' '}
        <strong>one 0–100 score with the research behind it</strong>, so you don&apos;t
        have to judge a list of ingredient names on your own.
      </>
    ),
    button: 'Get the full picture',
    surface: `ingredient_checker_medium_${placement}`,
  }
}

export default function IngredientCheckerInstallAsk({
  resultCount,
  highCount,
  scanCount,
  placement,
}: IngredientCheckerInstallAskProps) {
  const copy = selectCopy(resultCount, highCount, scanCount, placement)

  // The inline ask is deliberately more compact than the footer one: it sits
  // between the reader and their results, so it has to earn its space and get
  // out of the way. The footer ask has room to breathe.
  const isInline = placement === 'inline'

  return (
    <aside
      role="region"
      aria-label="Get the MicroPlastics app"
      className={[
        'not-prose relative overflow-hidden rounded-2xl border',
        'border-forest/30 bg-gradient-to-br from-[#1E6B47]/12 via-[#1E6B47]/5 to-white',
        isInline ? 'p-5 md:p-6 mb-5' : 'p-6 md:p-8 mt-8',
      ].join(' ')}
    >
      <p className="text-2xs font-semibold uppercase tracking-[0.18em] text-forest mb-1.5">
        {copy.eyebrow}
      </p>

      <h2
        className={[
          'font-bold text-gray-900 mb-2 leading-snug',
          isInline ? 'text-lg md:text-xl' : 'text-xl md:text-2xl',
        ].join(' ')}
      >
        {copy.headline}
      </h2>

      <p className="text-gray-700 leading-relaxed mb-4 max-w-2xl text-sm md:text-base">
        {copy.body}
      </p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <AppStoreLink
          source={copy.surface}
          ctaPosition={isInline ? 'tool_results_inline' : 'tool_results_footer'}
          trackingParams={{
            page_type: 'tool_ingredient_checker',
            result_count: resultCount,
            high_count: highCount,
            scan_count: scanCount,
          }}
          className="inline-flex items-center justify-center gap-2 bg-ink text-cream text-sm md:text-base font-semibold px-5 py-3 rounded-control hover:bg-ink/90 active:scale-95 transition-[color,background-color,border-color,text-decoration-color,transform] min-h-[44px] pressable"
        >
          <FaApple className="w-5 h-5" aria-hidden="true" />
          {copy.button}
        </AppStoreLink>

        {/* The free tier is the objection-handler. Saying it out loud next to
            the button removes the "is this going to cost me?" hesitation at the
            exact moment it forms. */}
        <p className="text-xs text-gray-600 leading-relaxed">
          Free on iOS. 3 scans, no account needed.
          <br className="hidden sm:block" />
          Reads the label from a photo, and scores the packaging too.
        </p>
      </div>
    </aside>
  )
}
